import React, {
  Dispatch, FormEvent, SetStateAction, useState,
} from 'react';
import styled from '@emotion/styled';
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Stack,
  TextField, Tooltip,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment, { Moment } from 'moment';
import { MdOutlineClose, MdHelpOutline } from 'react-icons/md';
import Divider from '@mui/material/Divider';
import { toast } from 'react-toastify';
import { serializeFormData } from '../../../common/util/serializeFormData';
import useAddAccountUserMutation, { AddAccountUserParams } from '../../hooks/useAddAccountUser';

const ModalHeader = styled.div`
  font-size: 20px;
  padding: 12px;
`;

const ModalBody = styled.div`
  display: flex;
  gap: 9px;
  flex-direction: column;
`;

const ModalDivider = styled(Divider)`
`;

const ModalHeaderActionContainer = styled(Stack)`
  justify-content: space-between;
`;

const CloseButton = styled(Button)`
  font-size: 20px;
  color: black;
`;

const SubmitButton = styled(Button)`
  width: 120px;
`;

const ErrorTypography = styled(Typography)`
  color: red;
`;

const LoginExpiresTypography = styled(Typography)`
  white-space: pre;
`;

const userTypeSelectOptions = ['Broker', 'Lender', 'Maintenance', 'MRO', 'OEM', 'Other', 'Pilot', 'Regulator'];

export type AddAccountUserModalStepOneModel = {
    firstName: string;
    lastName: string;
    email: string;
    userType: string;
    loginNeverExpires: boolean;
    loginExpirationDate: Date | null;
    role: number;
}

type AddAccountUserModalStepOneProps = {
    handleClose: () => void;
    goToNextStep: (model: AddAccountUserModalStepOneModel) => void;
    model: AddAccountUserModalStepOneModel;
    companyId: number;
    refreshUserList: () => void;
};

type LoginExpirationInfo =
    Pick<AddAccountUserModalStepOneModel, 'loginExpirationDate' | 'loginNeverExpires'>
    & { validationMessage: string; };

type ViewProps = Omit<AddAccountUserModalStepOneProps, 'goToNextStep' | 'companyId'> & {
    handleSubmit: (e: FormEvent<HTMLFormElement>) => void;
    loginExpiration: LoginExpirationInfo;
    setLoginExpiration: Dispatch<SetStateAction<LoginExpirationInfo>>;
    roleValidationMessage: string;
    setRoleValidationMessage: Dispatch<SetStateAction<string>>;
    emailValidationMessage: string;
    setEmailValidationMessage: Dispatch<SetStateAction<string>>;
    skipNextSteps: boolean;
    setSkipNextSteps: Dispatch<SetStateAction<boolean>>;
};

function View({
  handleClose, handleSubmit, loginExpiration, setLoginExpiration, roleValidationMessage, setRoleValidationMessage, model, emailValidationMessage,
  setEmailValidationMessage, skipNextSteps, setSkipNextSteps,
}: ViewProps): React.JSX.Element {
  return (
    <Dialog
      open
      onClose={handleClose}
      maxWidth="md"
      fullWidth
    >
      <form onSubmit={handleSubmit}>
        <ModalHeaderActionContainer spacing={2} direction="row">
          <ModalHeader>
            Add User
          </ModalHeader>
          <CloseButton variant="text" onClick={handleClose}>
            <MdOutlineClose />
          </CloseButton>
        </ModalHeaderActionContainer>
        <ModalDivider />
        <DialogContent>
          <ModalBody>
            <Grid container alignItems="center">
              <Grid item xs={2}>
                <InputLabel>First Name</InputLabel>
              </Grid>
              <Grid item xs={7}>
                <TextField
                  name="firstName"
                  size="small"
                  required
                  fullWidth
                  defaultValue={model.firstName}
                />
              </Grid>
            </Grid>

            <Grid container alignItems="center">
              <Grid item xs={2}>
                <InputLabel>Last Name</InputLabel>
              </Grid>
              <Grid item xs={7}>
                <TextField
                  name="lastName"
                  size="small"
                  required
                  fullWidth
                  defaultValue={model.lastName}
                />
              </Grid>
            </Grid>

            <Grid container alignItems="center">
              <Grid item xs={2}>
                <InputLabel>Email Address</InputLabel>
              </Grid>
              <Grid item xs={7}>
                <TextField
                  name="email"
                  size="small"
                  required
                  fullWidth
                  defaultValue={model.email}
                  onChange={() => setEmailValidationMessage('')}
                />
              </Grid>
              <Grid item xs={3}>
                {!!emailValidationMessage && (
                <ErrorTypography>
                  *
                  {' '}
                  {emailValidationMessage}
                </ErrorTypography>
                )}
              </Grid>
            </Grid>

            <Grid container alignItems="center">
              <Grid item xs={2}>
                <InputLabel>User Type</InputLabel>
              </Grid>
              <Grid item xs={7}>
                <Stack direction="row" alignItems="center">
                  <TextField
                    select
                    name="userType"
                    size="small"
                    required
                    fullWidth
                    defaultValue={model.userType}
                  >
                    {userTypeSelectOptions.map((opt) => (
                      <MenuItem key={opt} value={opt}>
                        <Typography>{opt}</Typography>
                      </MenuItem>
                    ))}
                  </TextField>
                  <Tooltip title={<Typography>This info helps us better understand who is using our system</Typography>}>
                    <IconButton>
                      <MdHelpOutline fontSize="28px" />
                    </IconButton>
                  </Tooltip>
                </Stack>

              </Grid>
            </Grid>

            <Grid container alignItems="center">
              <Grid item xs={2}>
                <InputLabel>Login Expires</InputLabel>
              </Grid>
              <Grid item xs={7}>
                <Stack direction="row" alignItems="center">
                  <Checkbox
                    name="loginNeverExpires"
                    checked={loginExpiration.loginNeverExpires}
                    onChange={(e) => setLoginExpiration({
                      loginExpirationDate: null,
                      loginNeverExpires: e.target.checked,
                      validationMessage: '',
                    })}
                  />
                  <LoginExpiresTypography>
                    Never, or
                    {'  '}
                  </LoginExpiresTypography>
                  <LocalizationProvider dateAdapter={AdapterMoment}>
                    <DatePicker
                      format="MM-DD-YYYY"
                      disabled={loginExpiration.loginNeverExpires}
                      minDate={moment()}
                      value={loginExpiration.loginExpirationDate ? moment(loginExpiration.loginExpirationDate) : null}
                      onChange={(x: Moment | null) => setLoginExpiration({
                        loginNeverExpires: false,
                        loginExpirationDate: x?.isValid() ? x.toDate() : null,
                        validationMessage: '',
                      })}
                      slotProps={{ textField: { size: 'small' } }}
                    />
                  </LocalizationProvider>
                </Stack>
              </Grid>
              <Grid item xs={3}>
                {!!loginExpiration.validationMessage && (
                <ErrorTypography>
                  *
                  {' '}
                  {loginExpiration.validationMessage}
                </ErrorTypography>
                )}
              </Grid>
            </Grid>

            <Grid container alignItems="start">
              <Grid item xs={2}>
                <InputLabel>Role</InputLabel>
              </Grid>
              <Grid item xs={7}>
                <RadioGroup
                  defaultValue={model.role}
                  name="role"
                  onChange={(e) => {
                    setRoleValidationMessage('');
                    setSkipNextSteps(e.target.value === '1' || e.target.value === '2');
                  }}
                >
                  <FormControlLabel
                    value={4}
                    control={<Radio />}
                    label="Viewer:  Access to view, search, and print all documents, but cannot make any edits"
                  />
                  <FormControlLabel
                    value={6}
                    control={<Radio />}
                    label="Buyer:  Access to view logbooks only, but cannot access Categories"
                  />
                  <FormControlLabel
                    value={7}
                    control={<Radio />}
                    label="MRO:  Access to upload, search, view, print, and download documents"
                  />
                  <FormControlLabel
                    value={3}
                    control={<Radio />}
                    label="Contributor:  Access to upload, view, and search all documents, but cannot make admin changes"
                  />
                  <FormControlLabel
                    value={2}
                    control={<Radio />}
                    label="Admin:  Full control over all admin functions, including managing users, access, aircraft, and logbooks"
                  />
                  <FormControlLabel
                    value={1}
                    control={<Radio />}
                    label="Owner"
                  />
                </RadioGroup>
              </Grid>
              <Grid item xs={2}>
                {!!roleValidationMessage && (
                <ErrorTypography>
                  *
                  {' '}
                  {roleValidationMessage}
                </ErrorTypography>
                )}
              </Grid>
            </Grid>
          </ModalBody>
        </DialogContent>
        <ModalDivider />
        <DialogActions>
          <Stack spacing={2} direction="row" justifyContent="end" width="100%">
            <SubmitButton variant="contained" color="primary" type="submit">
              {skipNextSteps ? 'Save' : 'Next'}
            </SubmitButton>
          </Stack>
        </DialogActions>
      </form>
    </Dialog>
  );
}

function Model({
  model, companyId, handleClose, goToNextStep, refreshUserList,
}: AddAccountUserModalStepOneProps): React.JSX.Element {
  const [loginExpiration, setLoginExpiration] = useState<LoginExpirationInfo>({
    loginNeverExpires: model.loginNeverExpires,
    loginExpirationDate: model.loginExpirationDate,
    validationMessage: '',
  });
  const [roleValidationMessage, setRoleValidationMessage] = useState('');
  const [emailValidationMessage, setEmailValidationMessage] = useState('');
  const [skipNextSteps, setSkipNextSteps] = useState(false);
  const addAccountUserMutation = useAddAccountUserMutation();

  function handleSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();

    const formValues = serializeFormData(e) as AddAccountUserModalStepOneModel;
    formValues.loginExpirationDate = loginExpiration.loginExpirationDate;
    formValues.loginNeverExpires = loginExpiration.loginNeverExpires;

    let isFormValid = true;

    if (!formValues.email) {
      setEmailValidationMessage('Required');
      isFormValid = false;
    } else if (!formValues.email.match(/^([\w.%+-]+)@([\w-]+\.)+([\w]{2,})$/i)) {
      setEmailValidationMessage('Invalid Email');
      isFormValid = false;
    }

    if (!formValues.role) {
      setRoleValidationMessage('Role is required');
      isFormValid = false;
    }

    if (!formValues.loginNeverExpires && !formValues.loginExpirationDate) {
      setLoginExpiration((p) => ({
        ...p,
        validationMessage: 'Required',
      }));
      isFormValid = false;
    }

    if (!isFormValid) {
      return;
    }

    if (formValues.loginExpirationDate) {
      formValues.loginExpirationDate.setHours(23, 59, 59);
    }

    formValues.role = Number(formValues.role);

    // If role is not Owner/Admin, proceed to aircraft assignment method step
    if (formValues.role !== 1 && formValues.role !== 2) {
      goToNextStep(formValues);
      return;
    }

    const params: AddAccountUserParams = {
      aircraft_assignment_method: 'all',
      aircrafts: null,
      fleets: null,
      company_id: companyId,
      first_name: formValues.firstName,
      last_name: formValues.lastName,
      email: formValues.email,
      login_expiry: formValues.loginNeverExpires ? null : formValues.loginExpirationDate.toISOString(),
      marketing_user_type: formValues.userType,
      role_id: formValues.role,
    };

    addAccountUserMutation.mutate(params, {
      onSuccess: () => {
        toast.success(`User '${formValues.firstName} ${formValues.lastName}' created`);
        refreshUserList();
        handleClose();
      },
      onError: () => {
        toast.error('Error creating user');
      },
    });
  }

  const viewProps: ViewProps = {
    handleSubmit,
    setLoginExpiration,
    loginExpiration,
    handleClose,
    roleValidationMessage,
    setRoleValidationMessage,
    model,
    emailValidationMessage,
    setEmailValidationMessage,
    skipNextSteps,
    setSkipNextSteps,
    refreshUserList,
  };

  return <View {...viewProps} />;
}

export default Model;
export { View };
